/**
 * SongTrash — v3.0.151
 *
 * A simple "recently deleted" bin for the current user. Shows every
 * song they've soft-deleted in the last 30 days, with per-row Restore
 * and Delete Permanently actions. Auto-purges via a Mongo TTL.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Trash2, RotateCcw, Music, Loader2, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiFetch } from '../App';

const fmtRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const fmtRemaining = (iso) => {
  if (!iso) return '';
  const deleted = new Date(iso).getTime();
  const purgeAt = deleted + 30 * 24 * 60 * 60 * 1000;
  const remaining = purgeAt - Date.now();
  if (remaining <= 0) return 'purging soon';
  const days = Math.floor(remaining / 86400000);
  if (days > 0) return `auto-purges in ${days}d`;
  const hours = Math.floor(remaining / 3600000);
  return `auto-purges in ${hours}h`;
};

export default function SongTrash() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/songs/trash');
      setSongs(Array.isArray(data?.songs) ? data.songs : []);
    } catch (err) { toast.error(err.message || 'Failed to load trash'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleRestore = async (songId) => {
    setBusyId(songId);
    try {
      await apiFetch(`/songs/${songId}/restore`, { method: 'POST' });
      setSongs((prev) => prev.filter((s) => s.song_id !== songId));
      toast.success('Restored back to your library');
    } catch (err) { toast.error(err.message || 'Restore failed'); }
    finally { setBusyId(null); }
  };

  const handleHardDelete = async (song) => {
    if (!window.confirm(
      `Permanently delete "${song.title || 'this song'}"?\n\n`
      + `This cannot be undone. The song will be removed from our servers immediately.`,
    )) return;
    setBusyId(song.song_id);
    try {
      await apiFetch(`/songs/${song.song_id}?permanent=true`, { method: 'DELETE' });
      setSongs((prev) => prev.filter((s) => s.song_id !== song.song_id));
      toast.success('Song deleted forever');
    } catch (err) { toast.error(err.message || 'Delete failed'); }
    finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 to-black text-white pb-32">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-6">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-rose-500/10 rounded-lg">
            <Trash2 className="text-rose-300" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" data-testid="trash-page-title">Recently deleted</h1>
            <p className="text-sm text-zinc-400">Songs stay here for 30 days after you delete them. Restore anytime.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-500">
            <Loader2 size={24} className="animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : songs.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-lg">
            <Trash2 size={40} className="text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Nothing here. Deleted songs will show up in this bin.</p>
          </div>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden">
            {songs.map((s) => (
              <div
                key={s.song_id}
                className="flex items-center gap-3 px-3 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5"
                data-testid={`trash-song-${s.song_id}`}
              >
                {s.image_url ? (
                  <img src={s.image_url} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center shrink-0">
                    <Music size={18} className="text-zinc-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{s.title || 'Untitled'}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                    <span>Deleted {fmtRelative(s.deleted_at)}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-amber-400/80 flex items-center gap-1">
                      <AlertTriangle size={10} /> {fmtRemaining(s.deleted_at)}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => handleRestore(s.song_id)}
                  disabled={busyId === s.song_id}
                  data-testid={`trash-restore-${s.song_id}`}
                  size="sm"
                  className="bg-emerald-500 text-black hover:bg-emerald-400"
                >
                  {busyId === s.song_id ? <Loader2 size={12} className="animate-spin mr-1" /> : <RotateCcw size={12} className="mr-1" />}
                  Restore
                </Button>
                <Button
                  onClick={() => handleHardDelete(s)}
                  disabled={busyId === s.song_id}
                  variant="outline"
                  size="sm"
                  data-testid={`trash-purge-${s.song_id}`}
                  className="border-rose-500/40 text-rose-300 hover:bg-rose-500/20"
                >
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
